import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  LifeBuoy,
  Lock,
  Mail,
  MessageSquare,
  Send,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import RelatedLinks from "@/components/common/RelatedLinks";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const FALLBACK_TOPICS = [
  { value: "safety", label: "Safety concern or report" },
  { value: "account", label: "Account or login help" },
  { value: "billing", label: "Membership or billing" },
  { value: "sponsor", label: "Question about sponsoring" },
  { value: "feedback", label: "Feedback or idea" },
  { value: "other", label: "Something else" },
];

export default function Contact() {
  const { user } = useAuth();
  const [topics, setTopics] = useState(FALLBACK_TOPICS);
  const [form, setForm] = useState({
    name: "",
    email: "",
    topic: "other",
    about_username: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/contact/topics")
      .then((res) => setTopics(res.data.topics || FALLBACK_TOPICS))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.display_name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [user]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Please add a name we can use when we reply.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return setError("Please enter an email address we can reply to.");
    if (form.message.trim().length < 20)
      return setError("Please add a little more detail (at least 20 characters).");

    setSending(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        topic: form.topic,
        message: form.message.trim(),
        about_username: form.about_username.trim() || null,
      });
      setSent(true);
      toast.success("Message sent. Thank you for reaching out.");
    } catch (err) {
      const msg = apiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const isSafety = form.topic === "safety";

  return (
    <div data-testid="contact-page" className="pb-4">
      <section className="bg-[#F7F4EF] border-b border-[#E3DED4]">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <span className="inline-flex items-center gap-2 rounded-full bg-white border border-[#E3DED4] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#1F6F78]">
            <Mail className="w-3.5 h-3.5" />
            Contact
          </span>
          <h1
            data-testid="contact-heading"
            className="mt-5 font-display text-4xl font-bold text-[#1F2A2E] leading-tight"
          >
            Get in touch with Sponsor Me
          </h1>
          <p
            data-testid="contact-definition"
            className="mt-5 text-lg text-[#1F2A2E] leading-8 max-w-2xl font-medium"
          >
            Contacting Sponsor Me means sending a message straight to the person who builds and
            maintains the platform.
          </p>
          <p className="mt-4 text-lg text-[#4B5A60] leading-8 max-w-2xl">
            Questions, safety concerns, billing problems or ideas - send a message. You do not need
            an account, and messages go straight to the Sponsor Me inbox.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 mt-10 grid gap-6 lg:grid-cols-[1fr_320px] items-start">
        {/* ------------------------------------------------------------- form */}
        <div className="rounded-3xl border border-[#E3DED4] bg-white p-6 sm:p-8">
          {sent ? (
            <div data-testid="contact-success" className="text-center py-8">
              <span className="w-14 h-14 rounded-full bg-[#E4F3EA] flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-[#14603C]" />
              </span>
              <h2 className="mt-4 font-display text-2xl font-bold text-[#1F2A2E]">
                Message sent
              </h2>
              <p className="mt-2 text-[#4B5A60] leading-7 max-w-md mx-auto">
                Thanks for reaching out, {form.name.trim() || "friend"}. We read every message and
                reply to {form.email.trim()} when a response is needed. Safety reports are reviewed
                first.
              </p>
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <Button
                  data-testid="contact-send-another-btn"
                  variant="outline"
                  className="border-[#E3DED4] text-[#1F6F78]"
                  onClick={() => {
                    setSent(false);
                    setForm((f) => ({ ...f, message: "", about_username: "" }));
                  }}
                >
                  Send another message
                </Button>
                <Button asChild className="bg-[#1F6F78] hover:bg-[#195B63] text-white">
                  <Link to="/sponsors">Browse sponsors</Link>
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="contact-name" className="text-[#1F2A2E] font-semibold">
                    Your name or initials
                  </Label>
                  <Input
                    id="contact-name"
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="e.g. Sam R."
                    maxLength={80}
                    className="mt-1.5 border-[#E3DED4] focus-visible:ring-[#1F6F78]"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-email" className="text-[#1F2A2E] font-semibold">
                    Email for our reply
                  </Label>
                  <Input
                    id="contact-email"
                    type="email"
                    data-testid="contact-email-input"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    className="mt-1.5 border-[#E3DED4] focus-visible:ring-[#1F6F78]"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#1F2A2E] font-semibold">What is this about?</Label>
                <Select value={form.topic} onValueChange={set("topic")}>
                  <SelectTrigger
                    data-testid="contact-topic-select"
                    className="mt-1.5 border-[#E3DED4] focus:ring-[#1F6F78]"
                  >
                    <SelectValue placeholder="Choose a topic" />
                  </SelectTrigger>
                  <SelectContent>
                    {topics.map((t) => (
                      <SelectItem
                        key={t.value}
                        value={t.value}
                        data-testid={`contact-topic-${t.value}`}
                      >
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isSafety && (
                <div
                  data-testid="contact-safety-notice"
                  className="flex gap-3 rounded-2xl border border-[#E6D3A3] bg-[#FDF6E7] p-4"
                >
                  <ShieldAlert className="w-5 h-5 text-[#8A5A2E] shrink-0 mt-0.5" />
                  <p className="text-sm text-[#6B4A22] leading-6">
                    Safety reports are reviewed first. If you are in immediate danger, contact your
                    local emergency services now -{" "}
                    <Link to="/resources" className="font-semibold underline">
                      crisis lines are listed here
                    </Link>
                    .
                  </p>
                </div>
              )}

              {(isSafety || form.topic === "account") && (
                <div>
                  <Label htmlFor="contact-about" className="text-[#1F2A2E] font-semibold">
                    Display name of the member involved{" "}
                    <span className="font-normal text-[#7A868B]">(optional)</span>
                  </Label>
                  <Input
                    id="contact-about"
                    data-testid="contact-about-input"
                    value={form.about_username}
                    onChange={set("about_username")}
                    placeholder="e.g. Mike R."
                    maxLength={80}
                    className="mt-1.5 border-[#E3DED4] focus-visible:ring-[#1F6F78]"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="contact-message" className="text-[#1F2A2E] font-semibold">
                  Your message
                </Label>
                <Textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={set("message")}
                  rows={7}
                  maxLength={4000}
                  placeholder="Tell us what is going on. Please do not include anything you would not want stored in our inbox."
                  className="mt-1.5 border-[#E3DED4] focus-visible:ring-[#1F6F78]"
                />
                <p className="mt-1 text-xs text-[#7A868B]">
                  {form.message.length}/4000 characters
                </p>
              </div>

              {error && (
                <p
                  data-testid="contact-error"
                  className="rounded-xl bg-[#FBE9E7] border border-[#E9C3BD] px-4 py-3 text-sm font-semibold text-[#9B2C1F]"
                >
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-btn"
                className="w-full sm:w-auto bg-[#1F6F78] hover:bg-[#195B63] text-white disabled:opacity-60"
              >
                <Send className="w-4 h-4 mr-1.5" />
                {sending ? "Sending..." : "Send message"}
              </Button>
            </form>
          )}
        </div>

        {/* ------------------------------------------------------------- aside */}
        <aside className="space-y-4">
          <div className="rounded-2xl border border-[#E3DED4] bg-white p-5" data-testid="contact-crisis-card">
            <span className="w-10 h-10 rounded-xl bg-[#1F2A2E] text-[#F7F4EF] flex items-center justify-center">
              <LifeBuoy className="w-5 h-5" />
            </span>
            <h2 className="mt-3 font-display text-lg font-bold text-[#1F2A2E]">
              In crisis right now?
            </h2>
            <p className="mt-2 text-sm text-[#4B5A60] leading-6">
              This inbox is not monitored around the clock and Sponsor Me is not an emergency
              service. Please use a crisis line or your local emergency number.
            </p>
            <Button
              asChild
              variant="outline"
              data-testid="contact-crisis-link"
              className="mt-4 w-full border-[#1F2A2E] text-[#1F2A2E] hover:bg-[#F2EFE8]"
            >
              <Link to="/resources">See crisis resources</Link>
            </Button>
          </div>

          <div className="rounded-2xl border border-[#E3DED4] bg-[#F7F4EF] p-5">
            <span className="w-10 h-10 rounded-xl bg-white border border-[#E3DED4] flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-[#1F6F78]" />
            </span>
            <h2 className="mt-3 font-display text-lg font-bold text-[#1F2A2E]">
              Reporting another member?
            </h2>
            <p className="mt-2 text-sm text-[#4B5A60] leading-6">
              If you are signed in, Block and Report inside the conversation is the fastest route -
              both are always free. Use this form if you cannot access your account.
            </p>
          </div>

          <div className="rounded-2xl border border-[#E3DED4] bg-white p-5">
            <span className="w-10 h-10 rounded-xl bg-[#E9F3F3] flex items-center justify-center">
              <Lock className="w-5 h-5 text-[#1F6F78]" />
            </span>
            <h2 className="mt-3 font-display text-lg font-bold text-[#1F2A2E]">
              What happens to your message
            </h2>
            <p className="mt-2 text-sm text-[#4B5A60] leading-6">
              Messages are stored in the Sponsor Me admin inbox and used only to answer you or act
              on a safety concern. See our{" "}
              <Link to="/privacy" className="font-semibold text-[#1F6F78] hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </aside>
      </div>
      <RelatedLinks
        heading="Keep reading"
        links={[
          { to: "/resources", label: "Crisis lines and recovery resources" },
          { to: "/about", label: "About Sponsor Me" },
          { to: "/sponsors", label: "Browse sponsors" },
        ]}
      />
    </div>
  );
}
